"use client";

import { useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Bell,
  Boxes,
  Check,
  ChevronDown,
  Mic,
  Move,
  PenTool,
  Play,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "@/lib/hooks/useAuth";
import { getUserNotifications } from "@/lib/api/notifications";
import dynamic from "next/dynamic";
import AnimatedClassroomPreview from "@/components/realtime-feedback/AnimatedClassroomPreview";
import RealtimeFeedbackSection from "@/components/landing/RealtimeFeedbackSection";

// 실제 3D 강의실 씬 — 무거우므로 클라이언트에서 지연 로드, 로딩 중엔 2D 애니메이션 표시
const HeroClassroomScene = dynamic(
  () => import("@/components/realtime-feedback/HeroClassroomScene"),
  { ssr: false, loading: () => <AnimatedClassroomPreview /> }
);

const CLASSROOM_FEATURES = [
  {
    icon: Mic,
    title: "실시간 음성 코칭",
    description:
      "표현, 호흡, 리듬처럼 글로 설명하기 어려운 부분을 대화하며 바로 교정합니다.",
  },
  {
    icon: UserRound,
    title: "1:1 집중 세션",
    description:
      "다른 수강생 없이 프로와 학생 한 명이 온전히 집중하는 강의실입니다.",
  },
  {
    icon: PenTool,
    title: "화이트보드 설명",
    description:
      "강사가 핵심 포인트와 연습 방향을 보드에 직접 표시하며 설명합니다.",
  },
  {
    icon: Move,
    title: "아바타 상호작용",
    description:
      "나만의 3D 아바타로 이동하고, 강사의 무대 호출을 받아 실습에 참여합니다.",
  },
] as const;

const RECOMMENDED_FOR = [
  "영상·문서 피드백만으로는 궁금증이 남았던 분",
  "내 소리를 들려주고 그 자리에서 교정받고 싶은 분",
  "오디션이나 공연을 앞두고 집중 점검이 필요한 분",
  "카메라 노출 부담 없이 편안하게 레슨받고 싶은 분",
] as const;

const FAQS = [
  {
    question: "카메라를 꼭 켜야 하나요?",
    answer:
      "아니요. 메타버스 강의실은 3D 아바타와 음성 중심으로 진행됩니다. 카메라를 켜지 않아도 참여할 수 있습니다.",
  },
  {
    question: "강의실에는 언제 들어갈 수 있나요?",
    answer:
      "실시간 피드백 신청 후 프로가 배정되고 일정이 확정되면, 강사가 강의실을 열어 초대 알림을 보냅니다. 알림 또는 이 페이지의 입장 버튼을 이용하세요.",
  },
  {
    question: "아바타를 미리 만들어야 하나요?",
    answer:
      "필수는 아닙니다. 설정하지 않으면 기본 캐릭터로 입장합니다. 프로필 수정에서 원하는 3D 캐릭터와 색상을 미리 설정할 수 있습니다.",
  },
  {
    question: "모바일에서도 참여할 수 있나요?",
    answer:
      "접속은 가능하지만 현재 이동 조작은 키보드 환경에 더 최적화되어 있습니다. 안정적인 수업을 위해 PC 또는 노트북 사용을 권장합니다.",
  },
] as const;

export default function RealtimeFeedbackLandingPage() {
  const router = useRouter();
  const { user, loading } = useAuth();
  const [entering, setEntering] = useState(false);

  const handleEnterClassroom = useCallback(async () => {
    if (loading) return;

    if (!user) {
      router.push("/auth/login?redirect=/realtime-feedback");
      return;
    }

    setEntering(true);
    try {
      const result = await getUserNotifications(user.uid, 30);
      if (!result.success || !result.notifications?.length) {
        toast(
          "아직 초대된 강의실이 없습니다. 실시간 피드백을 신청한 뒤, 강사 초대 알림을 확인해 주세요.",
          { icon: "🔔", duration: 5000 }
        );
        return;
      }

      const classroomInvite = result.notifications.find(n => {
        const link = n.link || "";
        const mode = (n.data as { mode?: string } | undefined)?.mode;
        return link.includes("/lessons/classroom/") || mode === "classroom";
      });

      if (!classroomInvite?.link) {
        toast(
          "강사가 메타버스 강의실을 열면 상단 알림(벨)으로 초대가 옵니다. 알림을 눌러 입장해 주세요.",
          { icon: "📬", duration: 5000 }
        );
        return;
      }

      router.push(classroomInvite.link);
    } catch {
      toast.error("강의실 정보를 불러오지 못했습니다.");
    } finally {
      setEntering(false);
    }
  }, [user, loading, router]);

  return (
    <main className="min-h-screen overflow-hidden bg-[#09090d] text-white">
      {/* Hero */}
      <section className="relative isolate border-b border-white/5">
        <div className="absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-[-18rem] h-[44rem] w-[44rem] -translate-x-1/2 rounded-full bg-violet-700/20 blur-[120px]" />
          <div className="absolute bottom-[-14rem] right-[-8rem] h-[32rem] w-[32rem] rounded-full bg-indigo-600/10 blur-[100px]" />
        </div>

        <div className="mx-auto grid min-h-[760px] max-w-7xl items-center gap-14 px-4 pb-20 pt-28 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-violet-400/25 bg-violet-400/10 px-4 py-2 text-sm font-medium text-violet-200">
              <Sparkles className="h-4 w-4" />
              ConnectTone Live
            </span>
            <h1 className="mt-7 break-keep text-4xl font-semibold leading-[1.15] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
              파일로는 놓치던 순간까지,
              <br />
              <span className="text-violet-300">실시간으로 피드백</span>
            </h1>
            <p className="mt-6 max-w-xl break-keep text-lg leading-8 text-white/65 sm:text-xl">
              프로와 1:1로 만나 질문하고, 직접 들려주고, 바로 교정받는 실시간
              음악 피드백 세션입니다.
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link href="/lessons/test-room">
                <button className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 font-semibold transition hover:bg-emerald-400 sm:w-auto">
                  <Users className="h-4 w-4" />
                  공개 테스트 강의실 입장
                </button>
              </Link>
              <Link href="/realtime-feedback/demo">
                <button className="flex w-full items-center justify-center gap-2 rounded-full bg-violet-500 px-7 py-3.5 font-semibold transition hover:bg-violet-400 sm:w-auto">
                  <Play className="h-4 w-4 fill-current" />
                  3분 체험해보기
                </button>
              </Link>
              <Link href="/expert-feedback?option=3">
                <button className="flex w-full items-center justify-center gap-2 rounded-full border border-violet-400/40 bg-violet-500/10 px-7 py-3.5 font-semibold transition hover:bg-violet-500/20 sm:w-auto">
                  실시간 피드백 신청하기
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <button
                type="button"
                onClick={handleEnterClassroom}
                disabled={entering}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-medium text-white/75 transition hover:bg-white/10 disabled:opacity-50 sm:w-auto"
              >
                {entering ? "초대 확인 중..." : "초대받은 강의실 입장"}
              </button>
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm text-white/45">
              <Bell className="h-4 w-4" />
              테스트 강의실은 로그인 후 누구나 입장 · 체험은 로그인 없이 가능
            </p>
          </motion.div>

          {/* Classroom preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="relative mx-auto w-full max-w-[600px]"
          >
            <HeroClassroomScene />
            <Link href="/realtime-feedback/demo" className="group">
              <div className="absolute -bottom-5 -left-3 flex items-center gap-2 rounded-2xl border border-white/10 bg-[#1b1a24]/95 px-4 py-3 shadow-xl backdrop-blur transition group-hover:border-violet-400/40 sm:-left-8">
                <div>
                  <p className="text-xs text-white/50">카메라 없이도</p>
                  <p className="mt-0.5 text-sm font-semibold">
                    나도 해보고 싶다면 →
                  </p>
                </div>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500 text-white transition group-hover:scale-105">
                  <Play className="h-3.5 w-3.5 fill-current" />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why realtime */}
      <section className="px-4 py-24 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet-300">
            Why realtime
          </p>
          <h2 className="mt-4 max-w-3xl break-keep text-3xl font-semibold tracking-tight sm:text-4xl">
            “어떻게 고쳐야 하죠?”를 바로 물을 수 있어야 하니까
          </h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            <div className="rounded-3xl border border-white/8 bg-white/[0.025] p-7 sm:p-9">
              <p className="text-sm font-medium text-white/40">일반 파일 피드백</p>
              <h3 className="mt-3 text-xl font-semibold text-white/70">
                결과를 받은 뒤 질문이 남습니다
              </h3>
              <ul className="mt-6 space-y-3 text-white/45">
                {["뉘앙스를 텍스트로 이해해야 함", "추가 질문과 답변에 시간 필요", "바로 다시 시도하기 어려움"].map(
                  item => (
                    <li key={item} className="flex gap-2">
                      <span className="text-white/25">—</span>
                      {item}
                    </li>
                  )
                )}
              </ul>
            </div>
            <div className="rounded-3xl border border-violet-400/25 bg-violet-400/[0.07] p-7 sm:p-9">
              <p className="text-sm font-medium text-violet-300">
                ConnectTone 실시간 1:1
              </p>
              <h3 className="mt-3 text-xl font-semibold">
                듣고, 질문하고, 다시 시도합니다
              </h3>
              <ul className="mt-6 space-y-3 text-white/75">
                {["프로의 설명을 음성으로 바로 확인", "궁금한 부분을 그 자리에서 질문", "교정 후 다시 들려주며 차이 확인"].map(
                  item => (
                    <li key={item} className="flex gap-2">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-violet-300" />
                      {item}
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-y border-white/5 bg-[#111117] py-24">
        <RealtimeFeedbackSection
          onEnter={handleEnterClassroom}
          entering={entering}
          compact
        />
      </section>

      {/* Features */}
      <section className="px-4 py-24 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet-300">
              Inside the classroom
            </p>
            <h2 className="mt-4 break-keep text-3xl font-semibold tracking-tight sm:text-4xl">
              실제 강의는 이렇게 이루어집니다
            </h2>
            <p className="mt-4 text-lg leading-8 text-white/55">
              단순한 화상 연결이 아니라, 음악 피드백에 필요한 상호작용을 한
              공간에 모았습니다.
            </p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            {CLASSROOM_FEATURES.map(feature => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="rounded-2xl border border-white/8 bg-white/[0.025] p-6 sm:p-7"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-400/10 text-violet-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold">{feature.title}</h3>
                  <p className="mt-2 break-keep leading-7 text-white/55">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recommended for */}
      <section className="px-4 pb-24 sm:px-6">
        <div className="mx-auto max-w-6xl rounded-[32px] border border-white/8 bg-gradient-to-br from-[#17151f] to-[#101016] p-7 sm:p-12">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet-300">
                Recommended for
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight">
                이런 분께 추천합니다
              </h2>
            </div>
            <div className="grid gap-3">
              {RECOMMENDED_FOR.map(item => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.025] px-4 py-4"
                >
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-violet-300" />
                  <span className="break-keep text-white/75">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/5 bg-[#111117] px-4 py-24 sm:px-6">
        <div className="mx-auto max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet-300">
            FAQ
          </p>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
            자주 묻는 질문
          </h2>
          <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
            {FAQS.map(item => (
              <details key={item.question} className="group py-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
                  {item.question}
                  <ChevronDown className="h-5 w-5 shrink-0 text-white/40 transition group-open:rotate-180" />
                </summary>
                <p className="max-w-2xl pb-1 pt-4 break-keep leading-7 text-white/55">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 py-24 text-center sm:px-6">
        <div className="mx-auto max-w-3xl">
          <Boxes className="mx-auto h-10 w-10 text-violet-300" />
          <h2 className="mt-6 break-keep text-3xl font-semibold tracking-tight sm:text-5xl">
            다음 피드백은,
            <br />
            직접 만나서 받아보세요
          </h2>
          <p className="mx-auto mt-5 max-w-xl break-keep text-lg text-white/55">
            신청부터 프로 매칭, 강의실 초대까지 커넥톤 안에서 이어집니다.
          </p>
          <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
            <Link href="/lessons/test-room">
              <button className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 font-semibold transition hover:bg-emerald-400 sm:w-auto">
                공개 테스트 강의실 입장
                <Users className="h-4 w-4" />
              </button>
            </Link>
            <Link href="/realtime-feedback/demo">
              <button className="flex w-full items-center justify-center gap-2 rounded-full bg-violet-500 px-7 py-3.5 font-semibold transition hover:bg-violet-400 sm:w-auto">
                3분 체험해보기
                <Play className="h-4 w-4 fill-current" />
              </button>
            </Link>
            <Link href="/expert-feedback?option=3">
              <button className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-semibold text-white/85 transition hover:bg-white/5 sm:w-auto">
                실시간 피드백 신청하기
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
